"use client";

// lib/geo/pais.js
// De dónde sale el país con el que la web elige moneda y lista de precios.
//
// Este bloque estaba copiado y pegado en DOCE pantallas (catálogo, checkout, packs,
// físicos, cada libro, clarinete, suscribirse, newsletter, login). Doce copias de la
// misma decisión significan que arreglar una deja once mal. Acá está una sola vez.
//
// La resolución es EXACTAMENTE la de antes, no cambia nada de lo que ve nadie:
//   1. cookie `cc`  -> la eligió la persona, o entró con ?cc= (que además la escribe)
//   2. cookie `geo` -> la puso CloudFront con el país del borde
//   3. RW           -> no sabemos de dónde es, y RW es la lista de precios en USD
//
// Lo único nuevo es que ahora queda ANOTADO cuál de los tres pasó. El paso 3 es el que
// importa: cuando cae ahí, a esa persona se le muestran dólares y hasta hoy era
// invisible, porque al que ve dólares y se va sin comprar no lo agarra ninguna tabla.
// Con esto podemos contarlos en vez de estimarlos con las compras.

import { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";

/** Sin país no hay lista propia: RW es la de dólares. */
export const PAIS_FALLBACK = "RW";

const RUTA_REPORTE = "/api/deteccion-pais";
const CLAVE_SESION = "bcm_deteccion_pais";

/**
 * El país tal como lo resuelve la web hoy, y de dónde salió. Pura: no manda nada ni
 * escribe nada, para poder llamarla dentro de un initializer de useState.
 *
 * @returns {{pais: string|null, fuente: "cc"|"geo"|"fallback"|"ssr"}}
 */
export function leerPais() {
  if (typeof window === "undefined") return { pais: null, fuente: "ssr" };

  const cc = Cookies.get("cc");
  if (cc) return { pais: cc.toUpperCase(), fuente: "cc" };

  const geo = Cookies.get("geo");
  if (geo) return { pais: geo.toUpperCase(), fuente: "geo" };

  return { pais: PAIS_FALLBACK, fuente: "fallback" };
}

/**
 * El país como string, y de paso lo anota. Para los lugares que lo piden fuera de un
 * render (un submit, un useEffect).
 *
 * `fallback` existe porque no todos quieren "RW": las pantallas de precio sí, pero el
 * newsletter y el login mandan el país al backend sólo para el huso del goteo de mails
 * y ahí "" (o sea, no sé) es la respuesta correcta, no "Ruanda".
 */
export function resolverPais({ fallback = PAIS_FALLBACK } = {}) {
  const { pais, fuente } = leerPais();
  anotar(fuente, pais);

  if (fuente === "ssr") return null;
  return fuente === "fallback" ? fallback : pais;
}

/**
 * El país para una pantalla de precios: mismo par [valor, setter] que tenía cada copia,
 * así el reemplazo es una línea y el resto del componente no se entera.
 *
 * El setter sigue existiendo porque varias pantallas lo pisan después con el ?cc= de la
 * URL. Eso es un override manual, no una detección: se anota lo que se DETECTÓ, y el
 * flag `forzado` del reporte marca las visitas con ?cc= para poder excluirlas.
 */
export function usePaisDetectado() {
  const deteccion = useRef(null);

  const [pais, setPais] = useState(() => {
    const d = leerPais();
    deteccion.current = d;
    return d.pais;
  });

  useEffect(() => {
    const d = deteccion.current || leerPais();
    anotar(d.fuente, d.pais);
  }, []);

  return [pais, setPais];
}

// ── El registro ─────────────────────────────────────────────────────────────
//
// Una vez por sesión y por combinación fuente+país, no una vez por página: lo que se
// quiere contar es gente, no clicks. Van las dos guardas porque el Set del módulo se
// pierde en cada navegación dura y sessionStorage puede estar bloqueado.
//
// Que una sesión anote DOS combinaciones no es un error y conviene no filtrarlo: si la
// primera página cayó en `fallback` y la segunda ya tenía `geo`, eso es exactamente el
// caso de "la cookie llegó tarde" y queremos verlo.

const anotadas = new Set();

function esNueva(clave) {
  if (anotadas.has(clave)) return false;
  anotadas.add(clave);

  try {
    const previas = String(window.sessionStorage.getItem(CLAVE_SESION) || "")
      .split("|")
      .filter(Boolean);
    if (previas.includes(clave)) return false;
    previas.push(clave);
    window.sessionStorage.setItem(CLAVE_SESION, previas.slice(-8).join("|"));
  } catch {
    /* sessionStorage bloqueado (modo privado, iframe): alcanza con el Set */
  }

  return true;
}

/** Anota la detección. Nunca tira: es telemetría y no puede romper una pantalla. */
export function anotar(fuente, pais) {
  if (typeof window === "undefined" || fuente === "ssr") return;
  if (!esNueva(`${fuente}:${pais || ""}`)) return;

  // Fuera del render y fuera del camino crítico: esto no puede demorar un precio.
  setTimeout(() => enviar(fuente, pais), 0);
}

function enviar(fuente, pais) {
  try {
    const cuerpo = new URLSearchParams({
      fuente,
      pais: pais || "",
      ruta: String(window.location.pathname || "").slice(0, 300),
      // Con ?cc= en la URL el país está forzado a mano (pruebas nuestras, o un link
      // armado). Se marca para poder sacarlo de la cuenta, no para descartarlo acá.
      forzado: new URLSearchParams(window.location.search).has("cc") ? "1" : "0",
    }).toString();

    // text/plain: no dispara preflight de CORS y sobrevive a que la pestaña se cierre.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        RUTA_REPORTE,
        new Blob([cuerpo], { type: "text/plain;charset=UTF-8" })
      );
      return;
    }

    fetch(RUTA_REPORTE, {
      method: "POST",
      body: cuerpo,
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* nunca romper la página por el reporte */
  }
}

/** Sólo para los tests: vacía la memoria de lo ya anotado. */
export function _resetAnotadas() {
  anotadas.clear();
}
